import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CategoryItem {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface SubcategoryItem extends CategoryItem {
  category_id?: string;
  type_id?: string;
}

/**
 * Hook for fetching expense categories
 */
export const useExpenseCategories = () => {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: async (): Promise<CategoryItem[]> => {
      const { data, error } = await supabase
        .from('properties_expenses_categories')
        .select('id, name, created_at, updated_at')
        .order('name');

      if (error) {
        console.error('Error fetching expense categories:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching expense subcategories
 */
export const useExpenseSubcategories = (categoryId?: string) => {
  return useQuery({
    queryKey: ['expense-subcategories', categoryId],
    queryFn: async (): Promise<SubcategoryItem[]> => {
      let query = supabase
        .from('properties_expenses_subcategories')
        .select('id, name, category_id, created_at, updated_at')
        .order('name');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching expense subcategories:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!categoryId || categoryId === undefined,
  });
};

/**
 * Hook for fetching business expense types
 */
export const useBusinessExpenseTypes = () => {
  return useQuery({
    queryKey: ['business-expense-types'],
    queryFn: async (): Promise<CategoryItem[]> => {
      const { data, error } = await supabase
        .from('business_expenses_types')
        .select('id, name, created_at, updated_at')
        .order('name');

      if (error) {
        console.error('Error fetching business expense types:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching business expense subtypes
 */
export const useBusinessExpenseSubtypes = (typeId?: string) => {
  return useQuery({
    queryKey: ['business-expense-subtypes', typeId],
    queryFn: async (): Promise<SubcategoryItem[]> => {
      let query = supabase
        .from('business_expenses_subtypes')
        .select('id, name, type_id, created_at, updated_at')
        .order('name');

      if (typeId) {
        query = query.eq('type_id', typeId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching business expense subtypes:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!typeId || typeId === undefined,
  });
};